import React, { useRef, useState, useEffect } from 'react';
import styled from 'astroturf/react';

import { Icon } from 'js/design_system/Icon';
import $image_url from 'js/utils/image_url';
import trackClick from 'js/utils/trackClick';
import { currentUser } from 'js/utils/user';
import useOnClickOutside from 'js/utils/hooks/useOnClickOutside';
import AccountMenu from 'js/header/FocusedNav/AccountMenu';
import DoubleDigitCountBubble from 'js/header/FocusedNav/DoubleDigitCountBubble';
import { AccountMenuProps } from 'js/header/FocusedNav/AccountMenu';
import AccountAvatarUpsellBadge from 'js/header/FocusedNav/AccountAvatarUpsellBadge';

const PositionedDoubleDigitCountBubble = styled(DoubleDigitCountBubble)`
  position: absolute;
  top: 2px;
  left: 30px;
`;

// Hover + selected (menu open) states mirror the DS secondary button.
const UserButton = styled.button<{ small?: boolean }>`
  position: relative;
  height: 48px;
  padding: 4px 16px;
  border-radius: var(--border-radius-radius-lg);
  font-size: 16px;
  font-weight: 500;
  background: #e4ebf7;
  border: 1px solid #cedbf1;
  color: #0645b1;
  cursor: pointer;
  flex-grow: 0;
  max-width: 200px;

  display: flex;
  align-items: center;

  &:active,
  &:visited {
    color: #0645b1;
  }
  &:hover {
    background: var(--button-secondary-fill-hover, #e6ecf7);
    color: var(--button-secondary-text-hover, #082f75);
    border-color: var(--button-secondary-stroke-hover, #386ac1);
  }
  &:active,
  &[aria-pressed='true'] {
    background: var(--button-secondary-fill-press, #cddaef);
    color: var(--button-secondary-text-press, #082f75);
    border-color: var(--button-primary-stroke-inverse-pressed, #0645b1);
    outline: 1px solid var(--button-primary-stroke-inverse-pressed, #0645b1);
  }
`;

// Wraps the avatar button so the upsell badge (an <a>) can be a SIBLING of the
// <button> rather than a child — interactive content nested in a button is invalid
// HTML. position:relative makes it the offset parent for the absolutely-positioned
// badge, so the visual placement is unchanged.
const AvatarButtonWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const ButtonProfilePic = styled.img`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  margin-right: 10px;
`;

const Caret = styled.div`
  margin-left: 6px;
  display: flex;
`;

const MenuContainer = styled.div`
  position: absolute;
  right: 0;
  top: 56px;
  z-index: 100;
  width: 240px;
  box-shadow: 0px 20px 40px 0px #00000033;
  border-radius: 4px;
  background: white;
  padding: 8px;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  color: #111111;
  text-align: initial;

  > * {
    margin: 8px;
  }
`;

const UserName = styled.div`
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

type AccountMenuButtonProps = AccountMenuProps & {
  totalCount: number;
  open: boolean;
  setOpen: (openness: boolean) => void;
  /**
   * Autotest `accountAvatarUpsell` surface (arm v_account_avatar_upsell). When
   * true and the user is non-premium, render a small additive "Upgrade" badge on
   * the avatar button. Defaults to false (CONTROL = today's rendering exactly).
   */
  accountAvatarUpsell?: boolean;
};

const AccountMenuButton = ({
  totalCount,
  open,
  setOpen,
  accountAvatarUpsell = false,
  ...accountMenuProps
}: AccountMenuButtonProps) => {
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  useOnClickOutside([buttonRef, menuRef], () => setOpen(false));

  const handleClick = () => {
    // Only track opening the menu, not closing it.
    if (!open) {
      trackClick({ source: 'nav-bar--account-menu-button' });
    }
    setOpen(!open);
  };

  return (
    <>
      <AvatarButtonWrapper>
        <UserButton ref={buttonRef} onClick={handleClick} aria-pressed={open}>
          {isClient && currentUser != null && (
            <ButtonProfilePic src={$image_url(currentUser.photo)} />
          )}
          <PositionedDoubleDigitCountBubble count={totalCount} />
          <UserName>{isClient && currentUser?.first_name}</UserName>
          <Caret>
            <Icon name="keyboard_arrow_down" />
          </Caret>
        </UserButton>
        {accountAvatarUpsell && !accountMenuProps.isPremium && (
          <AccountAvatarUpsellBadge />
        )}
      </AvatarButtonWrapper>
      {open && (
        <MenuContainer ref={menuRef}>
          <AccountMenu {...accountMenuProps} />
        </MenuContainer>
      )}
    </>
  );
};

export default AccountMenuButton;
